import React, { useContext, useMemo } from 'react';
import HeaderContainer from '../connectedForm/HeaderContainer';
import PurchaseOrderHeader from './PurchaseOrderHeader';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import { DangerButton, SecondaryButton } from '../connectedForm/Button';
import ActionButtons from '../../components/connectedForm/ActionButtons';
import { usePurchaseOrderFormActions } from '../../actions/purchasing/PurchaseOrderAction';
import getIn from '../../components/connectedForm/utils/getIn';
import styled from 'styled-components';
import BreadCrumbs from '../connectedForm/BreadCrumbs';
import { ISelectButtonProps, SAVE_ATTACHMENT_OPTIONS, SaveSelectButton } from '../SelectButton';
import ConnectedFormContext from '../Context/ConnectedFormContext';
// @ts-ignore
import { Analytics } from 'mixpanel';
import { IAlert, IPurchaseOrder } from '../../graphql/schema/p4types';
import { HeaderNotification } from '../Notification/HeaderNotification';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { useDeleteRecord } from '../connectedForm/ConnectedHeader';
import { usePristineEntity } from '../connectedForm/Hooks/pristineEntityHook';

const FlexDiv = styled.div`
  display: flex;
`;

const PurchaseActions = ({ noDeleteOption, deletePermitted, match, values, actionButtons, onClick }) => {
  const deleteButton =
    noDeleteOption || !deletePermitted || (match && match.params.id === 'New') ? null : (
      <DangerButton onClick={onClick}>Archive</DangerButton>
    );

  if (values && values.id !== null) {
    return (
      <FlexDiv key="purchase_order_header_key">
        {deleteButton}
        <ActionButtons actionButtons={actionButtons} values={values} />
      </FlexDiv>
    );
  }
  return null;
};

export type IPurchaseOrderHeaderComponentProps = {
  saveOptions: ISelectButtonProps['options'];
  orderNumber: number;
  statusFlags: Array<any>;
  values: IPurchaseOrder;
  onCancel: () => void;
  actionButtons: Array<any>;
  showActions: boolean;
  saveViaWrappingComponent?: boolean;
};

const PurchaseOrderHeaderComponent = (props: IPurchaseOrderHeaderComponentProps) => {
  const connectedForm = useContext(ConnectedFormContext);

  const handleSubmit = () => {
    //'saveViaWrappingComponent' prop is a hint that the parent component will save details later.
    //We call 'afterSubmit' because it should in turn close the modal & pass the form values to that parent.
    //Usage: When a PO is created from a Sales Order line, we want to hold off on saving the PO and do it alongside the Sales Order.
    if (props.saveViaWrappingComponent) {
      connectedForm.afterSubmit?.({ result: props.values });
    } else {
      connectedForm.submit?.();
      Analytics.track('Purchase order saved', {
        orderTotal: props?.values?.baseGrossGoodsTotal,
        numberOfLines: props?.values?.orderLines?.length ?? 0,
      });
    }
  };

  const [handleDelete, deleteModals] = useDeleteRecord({
    archive: true,
  });

  const [attachmentsPristine] = usePristineEntity({ values: props.values?.attachments });

  const supplier = getIn(props.values, 'supplier.name');

  const { noSaveOption, readOnly, noDeleteOption, deletePermitted } = props;

  const purchaseOrderSaveOptions: ISelectButtonProps['options'] = useMemo(
    () => [
      {
        value: SubmitOption.Exit,
        label: props.successButtonText || `Save ${props.field.toLowerCase()}`,
      },
      {
        value: SubmitOption.Continue,
        label: props.successButtonText || `Save ${props.field.toLowerCase()} & continue`,
      },
      {
        value: SubmitOption.New,
        label: props.successButtonText || `Save ${props.field.toLowerCase()} & new`,
        condition: (params) => `${params.id || params.uuid}`.toLowerCase() === 'new',
      },
    ],
    [props.field, props.successButtonText]
  );

  const successButton = noSaveOption ? null : (
    <SaveSelectButton
      onClick={handleSubmit}
      saveOptions={
        readOnly && !attachmentsPristine ? SAVE_ATTACHMENT_OPTIONS : props.saveOptions || purchaseOrderSaveOptions
      }
      saveReadOnlyCondition={(defaultReadOnly) => {
        if (attachmentsPristine) {
          return defaultReadOnly;
        }
        return false;
      }}
    />
  );

  const [actionButtons, modals] = usePurchaseOrderFormActions();

  const headerAlerts: Array<IAlert> | undefined = connectedForm.headerAlerts;
  const headerAlertElements = headerAlerts?.map((alert) => {
    return <HeaderNotification key={alert.uuid as string} alert={alert} />;
  });

  return (
    <>
      {modals}
      {deleteModals}
      <BreadCrumbs {...props} title="Order" />
      <HeaderContainer>
        <PurchaseOrderHeader
          optValue={supplier}
          orderNumber={props.orderNumber}
          statusFlags={props.statusFlags}
          values={props.values}
        />
        <HeaderAction>
          <SecondaryButton onClick={props.onCancel}>Cancel</SecondaryButton>
          {props.actionButtons ? <ActionButtons actionButtons={props.actionButtons} /> : null}
          {props.showActions ? (
            <PurchaseActions
              values={props.values}
              onClick={handleDelete}
              actionButtons={actionButtons}
              noDeleteOption={noDeleteOption}
              deletePermitted={deletePermitted}
            />
          ) : null}
          {successButton}
        </HeaderAction>
      </HeaderContainer>
      {headerAlertElements?.length && headerAlertElements?.length > 0 ? (
        <div
          style={{
            backgroundColor: '#faf9f8',
            paddingBottom: '4px',
            marginTop: '-20px',
          }}
        >
          {headerAlertElements}
        </div>
      ) : null}
    </>
  );
};

PurchaseOrderHeaderComponent.defaultProps = {
  showActions: true,
  field: 'Order',
  successButtonText: null,
};

export default PurchaseOrderHeaderComponent;
